import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import ProgressTable from './ProgressTable';
import { listChapters } from '../features/instructor/instructorApi';
import { listStudentSubmissionsInCourse } from '../features/progress/progressApi';
import type { StudentProgress, SubmissionRecord } from '../features/progress/progressApi';

interface StudentSubmissionHistoryProps {
  courseId: string;
  student: StudentProgress;
  chapterId?: string;
}

/** Drill-down detail view for a single student's submissions across a course (FR-029). */
function StudentSubmissionHistory({ courseId, student, chapterId }: StudentSubmissionHistoryProps) {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    async function loadSubmissions() {
      try {
        const result = chapterId
          ? await listStudentSubmissionsInCourse(courseId, student.userId, chapterId)
          : await listStudentSubmissionsInCourse(courseId, student.userId);
        if (!ignore) {
          setSubmissions(result);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Impossible de charger l'historique des soumissions.",
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadSubmissions();
    return () => {
      ignore = true;
    };
  }, [courseId, student.userId, chapterId]);

  return (
    <section aria-label={`Historique de ${student.name}`}>
      <h3>Historique de {student.name}</h3>
      {errorMessage && <p role="alert">{errorMessage}</p>}
      {loading && <p role="status">{t('instructor.progressLoading')}</p>}
      {submissions.length === 0 && !errorMessage && !loading && (
        <p>Aucune soumission pour cet étudiant.</p>
      )}
      <ul>
        {submissions.map((submission) => (
          <li key={submission.id}>
            {submission.exerciseTitle} — {submission.passed ? 'Réussi' : 'Échoué'} (
            {submission.pointsEarned} pts)
          </li>
        ))}
      </ul>
    </section>
  );
}

interface ProgressSectionProps {
  courseId: string;
  selectedStudent: StudentProgress | null;
  onSelectStudent: (student: StudentProgress | null) => void;
}

function ChapterProgressSelector({
  courseId,
  chapterId,
  onChange,
}: {
  courseId: string;
  chapterId: string;
  onChange: (chapterId: string) => void;
}) {
  const { t } = useTranslation();
  const chaptersQuery = useQuery({
    queryKey: ['instructor', 'chapters', courseId],
    queryFn: () => listChapters(courseId),
  });
  const chapters = chaptersQuery.data ?? [];
  const selectedChapter = chapters.find((chapter) => chapter.id === chapterId);

  return (
    <>
      {chaptersQuery.isPending && <p role="status">{t('instructor.chaptersLoading')}</p>}
      {chaptersQuery.isError && <p role="alert">{chaptersQuery.error.message}</p>}
      <label>
        {t('instructor.progressChapter')}
        <select
          value={chapterId}
          disabled={!chaptersQuery.isSuccess}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{t('instructor.allChapters')}</option>
          {chapters.map((chapter) => (
            <option key={chapter.id} value={chapter.id}>
              {chapter.title}
            </option>
          ))}
        </select>
      </label>
      {chaptersQuery.isSuccess && chapters.length === 0 && <p>{t('instructor.noChapters')}</p>}
      <p>
        {selectedChapter
          ? t('instructor.chapterProgressScope', { title: selectedChapter.title })
          : t('instructor.courseProgressScope')}
      </p>
    </>
  );
}

/** Progress table plus drill-down detail, wired per selected course (User Story 4). */
function ProgressSection({ courseId, selectedStudent, onSelectStudent }: ProgressSectionProps) {
  const [chapterId, setChapterId] = useState('');

  return (
    <section>
      <h2>Progression des étudiants</h2>
      <ChapterProgressSelector
        courseId={courseId}
        chapterId={chapterId}
        onChange={(value) => {
          setChapterId(value);
          onSelectStudent(null);
        }}
      />
      <ProgressTable
        key={`${courseId}:${chapterId}`}
        courseId={courseId}
        chapterId={chapterId || undefined}
        selectedStudentId={selectedStudent?.userId ?? null}
        onSelectStudent={onSelectStudent}
      />
      {selectedStudent && (
        <StudentSubmissionHistory
          key={`${courseId}:${chapterId}:${selectedStudent.userId}`}
          courseId={courseId}
          student={selectedStudent}
          chapterId={chapterId || undefined}
        />
      )}
    </section>
  );
}

export default ProgressSection;
